<template>
  <div class="admin-manage-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">SDK DEVICE ATTESTATION</p>
        <h1>SDK设备管理</h1>
        <p>查看机器人 TPM 公钥、SDK绑定状态、设备指纹和 DeRAS 阶段验签记录，用来确认任务动作来自登记过的工控机。</p>
      </div>
      <div class="header-actions">
        <div class="live-indicator" :class="{ paused: !autoRefreshEnabled }">
          <span></span>
          <div>
            <strong>{{ autoRefreshEnabled ? '自动刷新中' : '自动刷新已暂停' }}</strong>
            <small>{{ lastAutoRefreshAt ? `最近刷新 ${lastAutoRefreshAt}` : '进入页面后自动检测设备在线状态' }}</small>
          </div>
        </div>
        <button class="primary-btn" @click="fetchAll">刷新设备</button>
        <button class="secondary-btn" @click="toggleAutoRefresh">
          {{ autoRefreshEnabled ? '暂停自动刷新' : '开启自动刷新' }}
        </button>
      </div>
    </header>

    <section class="metric-grid">
      <article class="metric-card accent">
        <span>运维设备</span>
        <strong>{{ agentSummary.totalAgents || 0 }}</strong>
        <p>机器人 SDK Agent 总数</p>
      </article>
      <article class="metric-card">
        <span>SDK上报</span>
        <strong>{{ summary.totalReports || 0 }}</strong>
        <p>设备报告总数</p>
      </article>
      <article class="metric-card">
        <span>验签通过</span>
        <strong>{{ summary.verifiedReports || 0 }}</strong>
        <p>TPM签名通过</p>
      </article>
      <article class="metric-card">
        <span>已绑定</span>
        <strong>{{ summary.boundDevices || 0 }}</strong>
        <p>机器人记录已写入公钥</p>
      </article>
      <article class="metric-card">
        <span>用户已确认</span>
        <strong>{{ summary.confirmedDevices || 0 }}</strong>
        <p>注册人确认设备</p>
      </article>
      <article class="metric-card">
        <span>Agent在线</span>
        <strong>{{ agentSummary.onlineAgents || summary.onlineAgents || 0 }}</strong>
        <p>2分钟内心跳验签通过</p>
      </article>
      <article class="metric-card">
        <span>Agent离线</span>
        <strong>{{ agentSummary.offlineAgents || summary.offlineAgents || 0 }}</strong>
        <p>已确认但无有效心跳</p>
      </article>
      <article class="metric-card">
        <span>待安装/确认</span>
        <strong>{{ (agentSummary.waitingInstallAgents || 0) + (agentSummary.unconfirmedAgents || 0) }}</strong>
        <p>还没有形成可用 Agent</p>
      </article>
      <article class="metric-card danger">
        <span>心跳异常</span>
        <strong>{{ agentSummary.verifyFailedAgents || 0 }}</strong>
        <p>最近心跳验签失败</p>
      </article>
    </section>

    <section class="filter-card">
      <el-input v-model.trim="filters.keyword" clearable placeholder="搜索机器人 / CPU / 公钥指纹 / 设备指纹" @keyup.enter="fetchReports"></el-input>
      <el-input-number v-model="filters.robotId" :min="1" controls-position="right"></el-input-number>
      <el-select v-model="filters.signatureVerified" clearable placeholder="验签状态" @change="fetchReports">
        <el-option label="验签通过" :value="true"></el-option>
        <el-option label="验签失败" :value="false"></el-option>
      </el-select>
      <button class="primary-btn" @click="fetchReports">查询</button>
    </section>

    <section class="agent-filter-card">
      <el-input v-model.trim="agentFilters.keyword" clearable placeholder="搜索机器人 / 所有者 / TPM指纹 / 设备指纹" @keyup.enter="fetchAgents"></el-input>
      <el-select v-model="agentFilters.agentStatus" clearable placeholder="Agent状态" @change="fetchAgents">
        <el-option label="在线" value="ONLINE"></el-option>
        <el-option label="离线" value="OFFLINE"></el-option>
        <el-option label="等待安装" value="WAITING_INSTALL"></el-option>
        <el-option label="等待用户确认" value="UNCONFIRMED"></el-option>
        <el-option label="心跳验签异常" value="VERIFY_FAILED"></el-option>
      </el-select>
      <button class="primary-btn" @click="fetchAgents">查询设备</button>
    </section>

    <section class="table-card">
      <div class="section-title">
        <div>
          <p class="eyebrow">AGENT OPS</p>
          <h2>SDK Agent 运维视图</h2>
        </div>
        <span>按机器人查看在线、离线、最后心跳、SDK版本和 TPM 绑定状态</span>
      </div>
      <el-table v-loading="agentsLoading" :data="agents" @row-click="openAgent">
        <el-table-column label="机器人" min-width="190">
          <template #default="{ row }">
            <strong>{{ row.robotName || `机器人 #${row.robotId || '-'}` }}</strong>
            <small>ID {{ row.robotId || '-' }} · {{ row.robotCategory || '-' }} · {{ row.ownerName || row.ownerUserId || '-' }}</small>
            <small>地域：{{ row.serviceRegionLimit || '不限制' }}</small>
          </template>
        </el-table-column>
        <el-table-column label="Agent状态" width="150">
          <template #default="{ row }">
            <el-tag :type="agentOpsTag(row)">{{ row.agentStatusText || agentOpsLabel(row.agentStatus) }}</el-tag>
            <small>{{ agentHeartbeatText(row) }}</small>
          </template>
        </el-table-column>
        <el-table-column label="最后心跳" min-width="180">
          <template #default="{ row }">
            <span>{{ row.lastHeartbeatAt || '暂无心跳' }}</span>
            <small>{{ row.heartbeatIntervalSeconds ? `${row.heartbeatIntervalSeconds}s间隔` : '等待Agent上报' }}</small>
          </template>
        </el-table-column>
        <el-table-column label="SDK版本 / 设备" min-width="230">
          <template #default="{ row }">
            <span>{{ row.lastHeartbeatSdkVersion || row.latestReportSdkVersion || '-' }}</span>
            <small>{{ row.latestReportComputerName || '-' }} · {{ row.latestReportCpuModel || '-' }}</small>
          </template>
        </el-table-column>
        <el-table-column label="TPM公钥指纹" min-width="250">
          <template #default="{ row }">
            <code>{{ shortFingerprint(row.tpmPublicKeyFingerprint || row.latestReportPublicKeyFingerprint) }}</code>
            <small>心跳：{{ shortFingerprint(row.lastHeartbeatFingerprint) }}</small>
          </template>
        </el-table-column>
        <el-table-column label="最近验签" width="120">
          <template #default="{ row }">
            <el-tag :type="row.lastHeartbeatVerified ? 'success' : (row.lastHeartbeatVerified === false ? 'danger' : 'info')">
              {{ row.lastHeartbeatVerified ? '通过' : (row.lastHeartbeatVerified === false ? '失败' : '未上报') }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>
    </section>

    <section class="table-card">
      <div class="section-title">
        <div>
          <p class="eyebrow">TPM REPORTS</p>
          <h2>TPM设备上报</h2>
        </div>
        <span>点击行查看公钥和签名详情</span>
      </div>
      <el-table v-loading="reportsLoading" :data="reports" @row-click="openReport">
        <el-table-column prop="id" label="报告ID" width="90"></el-table-column>
        <el-table-column label="机器人" min-width="180">
          <template #default="{ row }">
            <strong>{{ row.robotName || `机器人 #${row.robotId || '-'}` }}</strong>
            <small>{{ row.robotCategory || '-' }} · {{ row.ownerName || row.ownerUserId || '-' }}</small>
          </template>
        </el-table-column>
        <el-table-column label="绑定状态" width="150">
          <template #default="{ row }">
            <el-tag :type="bindingTag(row)">{{ bindingLabel(row) }}</el-tag>
            <small>{{ row.robotRegisterStatus || '-' }}</small>
          </template>
        </el-table-column>
        <el-table-column label="Agent在线" width="170">
          <template #default="{ row }">
            <el-tag :type="agentTag(row)">{{ agentLabel(row) }}</el-tag>
            <small>{{ heartbeatText(row) }}</small>
          </template>
        </el-table-column>
        <el-table-column label="CPU / 设备" min-width="240">
          <template #default="{ row }">
            <span>{{ row.cpuModel || '-' }}</span>
            <small>{{ row.computerName || '-' }} · {{ row.robotSdkLastHeartbeatSdkVersion || row.sdkVersion || '-' }}</small>
          </template>
        </el-table-column>
        <el-table-column label="TPM公钥指纹" min-width="260">
          <template #default="{ row }">
            <code>{{ shortFingerprint(row.publicKeyFingerprint) }}</code>
            <small>绑定：{{ shortFingerprint(row.robotTpmPublicKeyFingerprint) }}</small>
          </template>
        </el-table-column>
        <el-table-column label="验签" width="110">
          <template #default="{ row }">
            <el-tag :type="row.signatureVerified ? 'success' : 'danger'">{{ row.signatureVerified ? '通过' : '失败' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="receivedAt" label="接收时间" min-width="170"></el-table-column>
      </el-table>
    </section>

    <section class="table-card">
      <div class="section-title">
        <div>
          <p class="eyebrow">DERAS VERIFY</p>
          <h2>阶段验签记录</h2>
        </div>
        <div class="stage-filters">
          <el-select v-model="verificationFilters.status" clearable placeholder="状态" @change="fetchVerifications">
            <el-option label="待SDK上报" value="PENDING"></el-option>
            <el-option label="通过" value="PASSED"></el-option>
            <el-option label="失败" value="FAILED"></el-option>
            <el-option label="已使用" value="USED"></el-option>
            <el-option label="过期" value="EXPIRED"></el-option>
          </el-select>
          <el-select v-model="verificationFilters.stage" clearable placeholder="阶段" @change="fetchVerifications">
            <el-option label="机器人出发" value="DEPARTED"></el-option>
            <el-option label="申请开始" value="START_REQUESTED"></el-option>
            <el-option label="申请结束" value="END_REQUESTED"></el-option>
          </el-select>
        </div>
      </div>
      <el-table v-loading="verificationsLoading" :data="verifications">
        <el-table-column prop="id" label="记录ID" width="90"></el-table-column>
        <el-table-column label="任务/订单" min-width="170">
          <template #default="{ row }">
            <strong>{{ row.taskTitle || `任务 #${row.taskId || '-'}` }}</strong>
            <small>订单 #{{ row.orderId || '-' }}</small>
          </template>
        </el-table-column>
        <el-table-column label="机器人" min-width="150">
          <template #default="{ row }">{{ row.robotName || row.robotId || '-' }}</template>
        </el-table-column>
        <el-table-column label="阶段" width="130">
          <template #default="{ row }">{{ stageLabel(row.stage) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="120">
          <template #default="{ row }">
            <el-tag :type="verificationTag(row.status)">{{ verificationStatusLabel(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="公钥指纹" min-width="220">
          <template #default="{ row }"><code>{{ shortFingerprint(row.publicKeyFingerprint) }}</code></template>
        </el-table-column>
        <el-table-column prop="verificationMessage" label="验签说明" min-width="220"></el-table-column>
        <el-table-column prop="createTime" label="创建时间" min-width="170"></el-table-column>
      </el-table>
    </section>

    <el-dialog title="SDK设备报告详情" v-model="reportVisible" width="820px" append-to-body>
      <div v-if="currentReport" class="report-detail">
        <div class="detail-grid">
          <div><span>机器人</span><strong>{{ currentReport.robotName || currentReport.robotId || '-' }}</strong></div>
          <div><span>所有者</span><strong>{{ currentReport.ownerName || currentReport.ownerUserId || '-' }}</strong></div>
          <div><span>CPU型号</span><strong>{{ currentReport.cpuModel || '-' }}</strong></div>
          <div><span>签名算法</span><strong>{{ currentReport.signatureAlgorithm || '-' }}</strong></div>
          <div><span>设备指纹</span><strong>{{ currentReport.machineFingerprint || '-' }}</strong></div>
          <div><span>TPM公钥指纹</span><strong>{{ currentReport.publicKeyFingerprint || '-' }}</strong></div>
          <div><span>Agent状态</span><strong>{{ agentLabel(currentReport) }}</strong></div>
          <div><span>最近心跳</span><strong>{{ heartbeatText(currentReport) }}</strong></div>
        </div>
        <h3>TPM 公钥 PEM</h3>
        <pre>{{ currentReport.publicKeyPem || '暂无公钥' }}</pre>
        <h3>验签说明</h3>
        <pre>{{ currentReport.verificationMessage || '暂无说明' }}</pre>
      </div>
    </el-dialog>

    <el-dialog title="SDK Agent 运维详情" v-model="agentVisible" width="820px" append-to-body>
      <div v-if="currentAgent" class="report-detail">
        <div class="agent-status-hero" :class="currentAgent.agentStatus">
          <span></span>
          <div>
            <strong>{{ currentAgent.agentStatusText || agentOpsLabel(currentAgent.agentStatus) }}</strong>
            <p>{{ agentHeartbeatText(currentAgent) }}</p>
          </div>
        </div>
        <div class="detail-grid">
          <div><span>机器人</span><strong>{{ currentAgent.robotName || currentAgent.robotId || '-' }}</strong></div>
          <div><span>所有者</span><strong>{{ currentAgent.ownerName || currentAgent.ownerUserId || '-' }}</strong></div>
          <div><span>地域限制</span><strong>{{ currentAgent.serviceRegionLimit || '不限制' }}</strong></div>
          <div><span>最后心跳</span><strong>{{ currentAgent.lastHeartbeatAt || '暂无心跳' }}</strong></div>
          <div><span>SDK版本</span><strong>{{ currentAgent.lastHeartbeatSdkVersion || currentAgent.latestReportSdkVersion || '-' }}</strong></div>
          <div><span>TPM公钥指纹</span><strong>{{ currentAgent.tpmPublicKeyFingerprint || '-' }}</strong></div>
          <div><span>心跳TPM指纹</span><strong>{{ currentAgent.lastHeartbeatFingerprint || '-' }}</strong></div>
          <div><span>设备指纹</span><strong>{{ currentAgent.lastHeartbeatMachineFingerprint || currentAgent.latestReportMachineFingerprint || '-' }}</strong></div>
          <div><span>最近验签</span><strong>{{ currentAgent.lastHeartbeatVerified ? '通过' : (currentAgent.lastHeartbeatVerified === false ? '失败' : '未上报') }}</strong></div>
        </div>
        <h3>最近心跳说明</h3>
        <pre>{{ currentAgent.lastHeartbeatMessage || '暂无心跳说明' }}</pre>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getAdminSdkAgents, getAdminSdkReports, getAdminSdkVerifications } from '../api/market'

export default {
  name: 'AdminSdkDevices',
  data() {
    return {
      reportsLoading: false,
      agentsLoading: false,
      verificationsLoading: false,
      agents: [],
      reports: [],
      verifications: [],
      summary: {},
      agentSummary: {},
      reportVisible: false,
      agentVisible: false,
      currentReport: null,
      currentAgent: null,
      autoRefreshEnabled: true,
      autoRefreshTimer: null,
      lastAutoRefreshAt: '',
      lastAgentStatusSnapshot: '',
      filters: {
        keyword: '',
        robotId: undefined,
        signatureVerified: ''
      },
      agentFilters: {
        keyword: '',
        agentStatus: ''
      },
      verificationFilters: {
        status: '',
        stage: ''
      }
    }
  },
  created() {
    this.fetchAll()
    this.startAutoRefresh()
  },
  beforeUnmount() {
    this.stopAutoRefresh()
  },
  methods: {
    fetchAll(options = {}) {
      this.fetchAgents(options)
      this.fetchReports(options)
      this.fetchVerifications(options)
    },
    startAutoRefresh() {
      this.stopAutoRefresh()
      if (!this.autoRefreshEnabled) return
      this.autoRefreshTimer = window.setInterval(() => {
        this.fetchAll({ silent: true })
      }, 10000)
    },
    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        window.clearInterval(this.autoRefreshTimer)
        this.autoRefreshTimer = null
      }
    },
    toggleAutoRefresh() {
      this.autoRefreshEnabled = !this.autoRefreshEnabled
      if (this.autoRefreshEnabled) {
        this.fetchAll({ silent: true })
        this.startAutoRefresh()
        this.$message.success('已开启设备状态自动刷新')
      } else {
        this.stopAutoRefresh()
        this.$message.info('已暂停设备状态自动刷新')
      }
    },
    async fetchAgents({ silent = false } = {}) {
      if (!silent) this.agentsLoading = true
      try {
        const response = await getAdminSdkAgents({
          keyword: this.agentFilters.keyword || undefined,
          agentStatus: this.agentFilters.agentStatus || undefined
        })
        if (response.data.code !== 200) {
          if (!silent) this.$message.error(response.data.msg || 'SDK Agent运维数据加载失败')
          return
        }
        const data = response.data.data || {}
        this.agents = data.records || []
        this.agentSummary = data.summary || {}
        this.lastAutoRefreshAt = this.formatClock(new Date())
        this.syncCurrentAgent()
        this.notifyAgentStatusChanges(this.agents, silent)
      } catch (error) {
        if (!silent) this.$message.error('SDK Agent运维数据请求失败')
      } finally {
        if (!silent) this.agentsLoading = false
      }
    },
    async fetchReports({ silent = false } = {}) {
      if (!silent) this.reportsLoading = true
      try {
        const response = await getAdminSdkReports({
          keyword: this.filters.keyword || undefined,
          robotId: this.filters.robotId || undefined,
          signatureVerified: this.filters.signatureVerified === '' ? undefined : this.filters.signatureVerified
        })
        if (response.data.code !== 200) {
          if (!silent) this.$message.error(response.data.msg || 'SDK设备报告加载失败')
          return
        }
        const data = response.data.data || {}
        this.reports = data.records || []
        this.summary = data.summary || {}
        this.syncCurrentReport()
      } catch (error) {
        if (!silent) this.$message.error('SDK设备报告请求失败')
      } finally {
        if (!silent) this.reportsLoading = false
      }
    },
    async fetchVerifications({ silent = false } = {}) {
      if (!silent) this.verificationsLoading = true
      try {
        const response = await getAdminSdkVerifications({
          robotId: this.filters.robotId || undefined,
          status: this.verificationFilters.status || undefined,
          stage: this.verificationFilters.stage || undefined
        })
        if (response.data.code !== 200) {
          if (!silent) this.$message.error(response.data.msg || '阶段验签记录加载失败')
          return
        }
        this.verifications = response.data.data || []
      } catch (error) {
        if (!silent) this.$message.error('阶段验签记录请求失败')
      } finally {
        if (!silent) this.verificationsLoading = false
      }
    },
    openReport(row) {
      this.currentReport = row
      this.reportVisible = true
    },
    openAgent(row) {
      this.currentAgent = row
      this.agentVisible = true
    },
    syncCurrentAgent() {
      if (!this.currentAgent || !this.currentAgent.robotId) return
      const latest = this.agents.find(item => String(item.robotId) === String(this.currentAgent.robotId))
      if (latest) this.currentAgent = latest
    },
    syncCurrentReport() {
      if (!this.currentReport || !this.currentReport.id) return
      const latest = this.reports.find(item => String(item.id) === String(this.currentReport.id))
      if (latest) this.currentReport = latest
    },
    notifyAgentStatusChanges(agents, silent) {
      const snapshot = (agents || [])
        .map(item => `${item.robotId || ''}:${item.agentStatus || ''}:${item.lastHeartbeatVerified}`)
        .join('|')
      if (!this.lastAgentStatusSnapshot) {
        this.lastAgentStatusSnapshot = snapshot
        return
      }
      if (snapshot === this.lastAgentStatusSnapshot) return

      const previous = this.parseAgentStatusSnapshot(this.lastAgentStatusSnapshot)
      this.lastAgentStatusSnapshot = snapshot
      if (silent) return

      const changed = (agents || []).find(item => {
        const oldStatus = previous[String(item.robotId || '')]
        return oldStatus && oldStatus.status !== item.agentStatus
      })
      if (!changed) return
      const name = changed.robotName || `机器人 #${changed.robotId}`
      this.$message.info(`${name} 状态更新为：${changed.agentStatusText || this.agentOpsLabel(changed.agentStatus)}`)
    },
    parseAgentStatusSnapshot(snapshot) {
      return String(snapshot || '')
        .split('|')
        .filter(Boolean)
        .reduce((map, item) => {
          const parts = item.split(':')
          map[parts[0]] = {
            status: parts[1],
            verified: parts[2]
          }
          return map
        }, {})
    },
    formatClock(date) {
      const pad = value => String(value).padStart(2, '0')
      return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
    shortFingerprint(value) {
      if (!value) return '-'
      return value.length > 22 ? `${value.slice(0, 12)}...${value.slice(-8)}` : value
    },
    bindingLabel(row) {
      if (row.robotSdkConfirmedAt) return '已确认'
      if (row.robotSdkBoundAt) return '已绑定'
      return '待确认'
    },
    bindingTag(row) {
      if (row.robotSdkConfirmedAt) return 'success'
      if (row.robotSdkBoundAt) return 'warning'
      return 'info'
    },
    agentLabel(row) {
      if (!row || !row.robotSdkConfirmedAt) return '未确认'
      if (row.robotSdkAgentOnline) return '在线'
      if (row.robotSdkLastHeartbeatVerified === false) return '验签异常'
      return '离线'
    },
    agentTag(row) {
      if (!row || !row.robotSdkConfirmedAt) return 'info'
      if (row.robotSdkAgentOnline) return 'success'
      if (row.robotSdkLastHeartbeatVerified === false) return 'danger'
      return 'warning'
    },
    heartbeatText(row) {
      if (!row || !row.robotSdkLastHeartbeatAt) {
        return row && row.robotSdkConfirmedAt ? '等待心跳' : '设备未确认'
      }
      const age = Number(row.robotSdkHeartbeatAgeSeconds)
      const ageText = Number.isFinite(age) ? this.formatHeartbeatAge(age) : row.robotSdkLastHeartbeatAt
      if (row.robotSdkAgentOnline) return `${ageText}前`
      if (row.robotSdkLastHeartbeatVerified === false) return row.robotSdkLastHeartbeatMessage || '心跳验签失败'
      return `${ageText}前最后心跳`
    },
    agentOpsLabel(value) {
      const labels = {
        ONLINE: 'SDK Agent在线',
        OFFLINE: 'SDK Agent离线',
        VERIFY_FAILED: '心跳验签异常',
        UNCONFIRMED: '等待用户确认',
        WAITING_INSTALL: '等待SDK安装'
      }
      return labels[value] || value || '未知状态'
    },
    agentOpsTag(row) {
      const status = row && row.agentStatus
      if (status === 'ONLINE') return 'success'
      if (status === 'VERIFY_FAILED') return 'danger'
      if (status === 'OFFLINE') return 'warning'
      return 'info'
    },
    agentHeartbeatText(row) {
      if (!row || !row.lastHeartbeatAt) {
        if (row && row.sdkConfirmedAt) return '等待心跳上报'
        if (row && row.sdkBoundAt) return '等待用户确认设备'
        return '等待SDK安装'
      }
      const age = Number(row.heartbeatAgeSeconds)
      const ageText = Number.isFinite(age) ? this.formatHeartbeatAge(age) : row.lastHeartbeatAt
      if (row.agentOnline) return `${ageText}前心跳验签通过`
      if (row.lastHeartbeatVerified === false) return row.lastHeartbeatMessage || '心跳验签失败'
      return `${ageText}前最后心跳`
    },
    formatHeartbeatAge(seconds) {
      const value = Math.max(0, Math.floor(seconds))
      if (value < 60) return `${value}秒`
      const minutes = Math.floor(value / 60)
      if (minutes < 60) return `${minutes}分钟`
      const hours = Math.floor(minutes / 60)
      return `${hours}小时`
    },
    stageLabel(value) {
      const labels = {
        DEPARTED: '机器人出发',
        START_REQUESTED: '申请开始',
        END_REQUESTED: '申请结束'
      }
      return labels[value] || value || '-'
    },
    verificationStatusLabel(value) {
      const labels = {
        PENDING: '待上报',
        PASSED: '通过',
        FAILED: '失败',
        USED: '已使用',
        EXPIRED: '过期'
      }
      return labels[value] || value || '-'
    },
    verificationTag(value) {
      if (value === 'PASSED' || value === 'USED') return 'success'
      if (value === 'FAILED') return 'danger'
      if (value === 'EXPIRED') return 'info'
      return 'warning'
    }
  }
}
</script>

<style scoped>
.admin-manage-page {
  min-height: 100vh;
  padding: 28px;
  color: #f6fbff;
}

.page-header,
.metric-card,
.filter-card,
.table-card {
  border: 1px solid rgba(143, 241, 199, 0.14);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.06);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding: 24px;
  margin-bottom: 18px;
}

.eyebrow {
  margin: 0 0 8px;
  color: #8ff1c7;
  font-size: 12px;
  letter-spacing: 0.18em;
}

.page-header h1 {
  margin: 0;
  font-size: 30px;
}

.page-header p,
.metric-card p,
small {
  display: block;
  margin-top: 7px;
  color: rgba(246, 251, 255, 0.58);
}

.header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.live-indicator {
  min-width: 220px;
  display: grid;
  grid-template-columns: 12px minmax(0, 1fr);
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid rgba(143, 241, 199, 0.22);
  border-radius: 16px;
  background: rgba(143, 241, 199, 0.08);
}

.live-indicator > span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #8ff1c7;
  box-shadow: 0 0 0 6px rgba(143, 241, 199, 0.13);
}

.live-indicator.paused {
  border-color: rgba(255, 208, 138, 0.22);
  background: rgba(255, 208, 138, 0.08);
}

.live-indicator.paused > span {
  background: #ffd08a;
  box-shadow: 0 0 0 6px rgba(255, 208, 138, 0.13);
}

.live-indicator strong {
  display: block;
  color: #f6fbff;
  font-size: 13px;
}

.live-indicator small {
  margin-top: 3px;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.metric-card {
  padding: 18px;
}

.metric-card.accent {
  border-color: rgba(255, 208, 138, 0.26);
  background: linear-gradient(135deg, rgba(255, 208, 138, 0.12), rgba(143, 241, 199, 0.08));
}

.metric-card.danger {
  border-color: rgba(255, 130, 130, 0.24);
  background: rgba(255, 130, 130, 0.08);
}

.metric-card span {
  color: rgba(143, 241, 199, 0.84);
}

.metric-card strong {
  display: block;
  margin-top: 10px;
  font-size: 30px;
}

.filter-card {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 150px 150px 110px;
  gap: 12px;
  padding: 16px;
  margin-bottom: 18px;
}

.agent-filter-card {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 180px 120px;
  gap: 12px;
  padding: 16px;
  margin-bottom: 18px;
  border: 1px solid rgba(255, 208, 138, 0.16);
  border-radius: 20px;
  background: rgba(255, 208, 138, 0.06);
}

.table-card {
  padding: 16px;
  margin-bottom: 18px;
}

.section-title {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
}

.section-title h2 {
  margin: 0;
  font-size: 22px;
}

.section-title span {
  color: rgba(246, 251, 255, 0.58);
}

.stage-filters {
  display: flex;
  gap: 10px;
}

.primary-btn {
  height: 40px;
  min-width: 108px;
  border: none;
  border-radius: 999px;
  color: #03101d;
  font-weight: 800;
  cursor: pointer;
  background: linear-gradient(135deg, #8ff1c7, #ffd08a);
}

.secondary-btn {
  height: 40px;
  min-width: 118px;
  border: 1px solid rgba(143, 241, 199, 0.22);
  border-radius: 999px;
  color: #dff8ff;
  font-weight: 800;
  cursor: pointer;
  background: rgba(143, 241, 199, 0.08);
}

code {
  color: #8ff1c7;
  font-family: ui-monospace, monospace;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.detail-grid div {
  padding: 14px;
  border-radius: 14px;
  background: #f6f8fb;
}

.detail-grid span {
  display: block;
  color: #6b7280;
  font-size: 12px;
}

.detail-grid strong {
  display: block;
  margin-top: 6px;
  color: #101827;
  word-break: break-all;
}

.report-detail h3 {
  margin: 16px 0 8px;
  color: #101827;
}

.report-detail pre {
  max-height: 240px;
  overflow: auto;
  padding: 14px;
  border-radius: 12px;
  color: #101827;
  white-space: pre-wrap;
  word-break: break-all;
  background: #eef3f8;
}

.agent-status-hero {
  display: flex;
  gap: 14px;
  align-items: center;
  margin-bottom: 14px;
  padding: 16px;
  border-radius: 16px;
  color: #101827;
  background: #f6f8fb;
}

.agent-status-hero > span {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #9ca3af;
  box-shadow: 0 0 0 6px rgba(156, 163, 175, 0.12);
}

.agent-status-hero strong {
  display: block;
  font-size: 18px;
}

.agent-status-hero p {
  margin: 4px 0 0;
  color: #64748b;
}

.agent-status-hero.ONLINE > span {
  background: #18b981;
  box-shadow: 0 0 0 6px rgba(24, 185, 129, 0.14);
}

.agent-status-hero.OFFLINE > span,
.agent-status-hero.UNCONFIRMED > span,
.agent-status-hero.WAITING_INSTALL > span {
  background: #f59e0b;
  box-shadow: 0 0 0 6px rgba(245, 158, 11, 0.14);
}

.agent-status-hero.VERIFY_FAILED > span {
  background: #ef4444;
  box-shadow: 0 0 0 6px rgba(239, 68, 68, 0.14);
}

@media (max-width: 980px) {
  .metric-grid,
  .filter-card,
  .agent-filter-card {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .admin-manage-page {
    padding: 18px;
  }

  .page-header,
  .metric-grid,
  .filter-card,
  .agent-filter-card,
  .header-actions,
  .detail-grid,
  .section-title {
    grid-template-columns: 1fr;
    flex-direction: column;
    align-items: stretch;
  }

  .live-indicator {
    min-width: 0;
  }
}
</style>
